// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// THE hero view. Answers: which day should I buy, and is that still true?
// Click a year in the ribbon to see that year's cycle shape; click a region to
// switch the whole view.

import { loadCycle, loadMeta, prefs } from '../data';
import type { CycleRegion, Meta, RegimeYear } from '../types';
import { chartSvg, legend, linePath, mark, svgEl, text, ticks } from '../chart';
import { gridCells } from '../layout';
import { DAY_SHORT, DAY_NAMES, cents, deltaLabel, divergingColour, esc, escTip, tankSaving } from '../format';
import { glossaryTerm } from '../glossary';
import { attachSvgZoom } from '../svgZoom';

let selectedYear: number | null = null;

export async function renderCycle(root: HTMLElement): Promise<void> {
  root.innerHTML = '<div class="skeleton"></div>';
  const [cycle, meta] = await Promise.all([loadCycle(), loadMeta()]);
  const weekly = cycle.regions.filter((r) => r.verdict === 'weekly');
  const long = cycle.regions.filter((r) => r.verdict === 'long');
  const none = cycle.regions.filter((r) => r.verdict === 'none');

  const stored = prefs.get('cycleRegion', 'WA|Metro');
  let region = cycle.regions.find((r) => `${r.state}|${r.region}` === stored) ?? weekly[0] ?? cycle.regions[0];
  selectedYear = null;

  const options = [...weekly, ...long, ...none];
  root.innerHTML = `
    <div class="view-head">
      <h1>The cheapest day keeps moving</h1>
      <p>Petrol prices in Perth do not drift, they saw-tooth — a ${glossaryTerm('cycle', 'price cycle')} exactly seven days long.
      But the cheapest day of that week has changed three times in twenty-five years, and most people are still using the old rule.</p>
    </div>
    <div id="headline"></div>
    <div class="panel">
      <div class="controls">
        <label for="cycle-region">Region</label>
        <select id="cycle-region">
          ${optGroup('Seven-day cycle — a cheapest day exists', weekly, region)}
          ${optGroup('Longer cycle — no single cheapest day', long, region)}
          ${optGroup('No usable cycle — the price barely moves', none, region)}
        </select>
        <span class="brand-sub" id="cycle-verdict"></span>
      </div>
      <div id="ribbon-panel"></div>
    </div>
    <div class="grid-2">
      <div class="panel" id="profile-panel"></div>
      <div class="panel" id="weeks-panel"></div>
    </div>
  `;

  renderHeadline(root.querySelector('#headline')!, cycle, meta);
  const select = root.querySelector('#cycle-region') as HTMLSelectElement;
  select.value = `${region.state}|${region.region}`;
  select.addEventListener('change', () => {
    const next = cycle.regions.find((r) => `${r.state}|${r.region}` === select.value);
    if (!next) return;
    region = next;
    selectedYear = null;
    prefs.set('cycleRegion', select.value);
    draw();
  });

  function draw(): void {
    root.querySelector('#cycle-verdict')!.textContent = verdictLine(region);
    renderRibbon(root.querySelector('#ribbon-panel')!, region, () => draw());
    renderProfile(root.querySelector('#profile-panel')!, region);
    renderWeeks(root.querySelector('#weeks-panel')!, region);
  }
  draw();
  void options;
}

function optGroup(label: string, list: CycleRegion[], current: CycleRegion): string {
  if (!list.length) return '';
  return `<optgroup label="${esc(label)}">${list.map((r) => {
    const key = `${r.state}|${r.region}`;
    return `<option value="${esc(key)}"${key === `${current.state}|${current.region}` ? ' selected' : ''}>${esc(r.region)} (${r.state})</option>`;
  }).join('')}</optgroup>`;
}

function verdictLine(r: CycleRegion): string {
  if (r.verdict === 'weekly') return `Seven-day cycle · ${r.period ? `${r.period.median} days between price hikes` : ''}`;
  if (r.verdict === 'long') return `Cycle runs about ${r.period ? Math.round(r.period.median) : '?'} days — longer than a week, so no weekly rule applies`;
  return 'The price here barely moves week to week — there is no cycle to time';
}

function renderHeadline(host: HTMLElement, cycle: { regions: CycleRegion[] }, meta: Meta): void {
  const perth = cycle.regions.find((r) => r.state === 'WA' && r.region === 'Metro');
  const headline = meta.headline;
  if (!perth || !headline || headline.eras.length < 2) { host.innerHTML = ''; return; }
  const eras = headline.eras;
  const now = eras[eras.length - 1];
  const prev = eras[eras.length - 2];
  const latest = perth.years[perth.years.length - 1];
  const hike = latest.medianHikeSize ?? 0;
  host.innerHTML = `
    <div class="headline">
      <h2>In Perth the day to fill up is ${esc(now.buy)} — but from ${esc(String(prev.from))} to ${esc(String(prev.to))} it was ${esc(prev.buy)}, and ${esc(now.buy)} was the most expensive day of the week.</h2>
      <p>The price jumps on ${esc(now.hike)} morning and drifts down for the rest of the week, so the cheapest moment is the day before the jump.
      That day has moved three times since 2001. Anyone still using the rule they learned last decade has been buying on the wrong day ever since.</p>
      <div class="stat-row">
        <div class="stat"><b>${esc(now.buy.slice(0, 3))}</b><span>the day to buy, ${esc(String(now.from))}–${esc(String(headline.latestYear))}</span></div>
        <div class="stat"><b>${cents(hike)}c</b><span>typical ${esc(now.hike)} jump, ${esc(String(headline.latestYear))}</span></div>
        <div class="stat"><b>${tankSaving(hike)}</b><span>on a 55-litre tank</span></div>
        <div class="stat"><b>${Math.round(latest.hikeShare * 100)}%</b><span>of weeks it jumped on that day this year</span></div>
      </div>
    </div>
  `;
}

/**
 * The regime ribbon: one row per year, seven columns for the days, coloured by
 * how far that day sits from its own week's average. The cheap band visibly
 * migrates across the chart, which is the entire point of the view.
 */
function renderRibbon(host: HTMLElement, region: CycleRegion, onSelect: () => void): void {
  const years = region.years;
  const rowH = 24;
  const padL = 58;
  const padR = 16;
  const padT = 34;
  const padB = 30;
  // Sized to the panel it lands in, so the ribbon fills a desktop layout
  // instead of hugging the left edge, and scrolls locally below that.
  const avail = Math.max(560, Math.min(1180, (host.clientWidth || 900) - 34));
  const gridW = Math.max(7 * 74, avail - padL - padR);
  const width = padL + gridW + padR;
  const height = padT + years.length * rowH + padB;

  host.innerHTML = `
    <div class="panel-head">
      <h2>Twenty-five years of the weekly cycle</h2>
      <p>Each row is a year, each column a day. Colour is how far that day sat from its own week's average —
      ${glossaryTerm('detrended')} so the oil price cannot fake a pattern. Green is cheap, red is dear.
      Click a year to see its shape.</p>
    </div>
    <div class="chart-scroll"><div class="chart-frame" id="ribbon-frame"></div></div>
    <div id="ribbon-legend"></div>
  `;
  const frame = host.querySelector('#ribbon-frame') as HTMLElement;
  const svg = chartSvg(width, height, `Weekly price cycle by year for ${region.region}`);

  for (let d = 0; d < 7; d++) {
    svg.appendChild(text(padL + (d + 0.5) * (gridW / 7), padT - 12, DAY_SHORT[d], { 'text-anchor': 'middle', 'font-size': 12, 'font-weight': 600 }));
  }

  const all = years.flatMap((y) => y.profile);
  const lim = Math.max(5, ...all.map(Math.abs));
  const cells = gridCells(7, years.length, gridW, years.length * rowH, 1.5);

  for (const cell of cells) {
    const y = years[cell.row];
    const dev = y.profile[cell.col];
    const usable = y.rankedWeeks >= 8;
    const colour = usable ? divergingColour(dev, lim) : '#eceae5';
    const isBuy = usable && y.buyDay === cell.col;
    const rect = svgEl('rect', {
      x: padL + cell.x, y: padT + cell.y, width: cell.w, height: cell.h,
      fill: colour, rx: 2,
      stroke: isBuy ? '#0f172a' : 'none', 'stroke-width': isBuy ? 1.6 : 0,
    });
    const tip = usable
      ? `${y.year} · ${DAY_NAMES[cell.col]}\n${deltaLabel(dev)} vs the week average\nbuy day ${y.buyDay != null ? DAY_NAMES[y.buyDay] : '—'} — the price jumped on ${y.modalHike != null ? DAY_NAMES[y.modalHike] : '—'} in ${Math.round(y.hikeShare * 100)}% of weeks\n${y.rankedWeeks} weeks with a cycle, ${y.flatWeeks} flat`
      : `${y.year} · ${DAY_NAMES[cell.col]}\nno usable cycle this year (${y.flatWeeks} of ${y.weeks} weeks were flat)`;
    mark(rect, tip);
    rect.addEventListener('click', () => { selectedYear = selectedYear === y.year ? null : y.year; onSelect(); });
    if (selectedYear != null && selectedYear !== y.year) rect.classList.add('dim');
    svg.appendChild(rect);
  }

  for (let i = 0; i < years.length; i++) {
    if (years.length > 14 && i % 2 === 1 && i !== years.length - 1) continue;
    svg.appendChild(text(padL - 8, padT + i * rowH + rowH / 2 + 4, String(years[i].year), { 'text-anchor': 'end', 'font-size': 12 }));
  }
  svg.appendChild(text(padL, height - 8, 'Outlined cell = the day to buy that year (the day before the price jumped)', { 'font-size': 11, fill: '#857f78' }));

  frame.appendChild(svg);
  attachSvgZoom(svg, { maxScale: 4 });

  host.querySelector('#ribbon-legend')!.appendChild(legend([
    { label: `${cents(lim)}c below the week average`, colour: '#0b6e4f' },
    { label: 'at the week average', colour: '#f0ece2' },
    { label: `${cents(lim)}c above`, colour: '#a83a17' },
    { label: 'no usable cycle that year', colour: '#eceae5' },
  ]));
}

/** The selected (or latest) year's cycle shape, as a seven-point profile. */
function renderProfile(host: HTMLElement, region: CycleRegion): void {
  const year: RegimeYear | undefined = selectedYear != null
    ? region.years.find((y) => y.year === selectedYear)
    : region.years[region.years.length - 1];
  if (!year) { host.innerHTML = '<p class="empty">No data for this region.</p>'; return; }

  const width = 460;
  const height = 260;
  const padL = 46; const padR = 14; const padT = 16; const padB = 46;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;

  host.innerHTML = `
    <div class="panel-head">
      <h2>${esc(String(year.year))} — the shape of the week</h2>
      <p>How much each day cost relative to its own week's average, in cents per litre.
      Based on ${year.rankedWeeks} weeks that had a cycle${year.flatWeeks ? ` (${year.flatWeeks} flat weeks excluded)` : ''}.</p>
    </div>
    <div class="chart-scroll"><div class="chart-frame" id="profile-frame"></div></div>
    <p class="brand-sub" id="profile-note" style="margin-top:8px"></p>
  `;
  const svg = chartSvg(width, height, `Cycle shape for ${region.region} in ${year.year}`);
  const lim = Math.max(2, ...year.profile.map(Math.abs)) * 1.15;
  const yFor = (v: number) => padT + innerH / 2 - (v / lim) * (innerH / 2);

  for (const t of ticks(-lim, lim, 5)) {
    svg.appendChild(svgEl('line', { x1: padL, x2: padL + innerW, y1: yFor(t), y2: yFor(t), class: 'gridline' }));
    svg.appendChild(text(padL - 8, yFor(t) + 4, `${t > 0 ? '+' : ''}${(t / 10).toFixed(1)}`, { 'text-anchor': 'end', 'font-size': 11 }));
  }
  svg.appendChild(svgEl('line', { x1: padL, x2: padL + innerW, y1: yFor(0), y2: yFor(0), stroke: '#a8a094', 'stroke-width': 1 }));

  const bw = innerW / 7;
  year.profile.forEach((dev, d) => {
    const x = padL + d * bw + bw * 0.18;
    const w = bw * 0.64;
    const y0 = yFor(0);
    const y1 = yFor(dev);
    const bar = svgEl('rect', {
      x, y: Math.min(y0, y1), width: w, height: Math.max(1, Math.abs(y1 - y0)),
      fill: dev <= 0 ? '#167a4a' : '#c2410c', rx: 2,
    });
    mark(bar, `${DAY_NAMES[d]} ${year.year}\n${deltaLabel(dev)} vs the week average${year.buyDay === d ? '\nthe day to buy' : ''}${year.modalHike === d ? '\nthe price jumped on this day' : ''}`);
    svg.appendChild(bar);
    svg.appendChild(text(padL + d * bw + bw / 2, height - 26, DAY_SHORT[d], {
      'text-anchor': 'middle', 'font-size': 12,
      'font-weight': year.buyDay === d ? 700 : 400,
      fill: year.buyDay === d ? '#167a4a' : '#57534e',
    }));
  });
  svg.appendChild(text(width / 2, height - 8, 'cents per litre away from that week’s own average', { 'text-anchor': 'middle', 'font-size': 11, fill: '#857f78' }));
  (host.querySelector('#profile-frame') as HTMLElement).appendChild(svg);

  const note = host.querySelector('#profile-note')!;
  if (year.rankedWeeks < 8 || year.buyDay == null) {
    note.textContent = `${region.region} had no usable weekly cycle in ${year.year}: ${year.flatWeeks} of ${year.weeks} weeks moved less than 2 cents from top to bottom, so there is no day worth naming.`;
  } else {
    note.textContent = `The price jumped on ${DAY_NAMES[year.modalHike ?? 0]} in ${Math.round(year.hikeShare * 100)}% of weeks — chance alone would be about 14% — so ${DAY_NAMES[year.buyDay]} was the day to buy, typically ${cents(year.medianHikeSize ?? 0)}c below the next morning.`;
  }
}

/** The last three years of actual weeks, as the raw saw-tooth. */
function renderWeeks(host: HTMLElement, region: CycleRegion): void {
  const weeks = region.recentWeeks.slice(-52);
  host.innerHTML = `
    <div class="panel-head">
      <h2>The last year, week by week</h2>
      <p>Every complete week, drawn end to end. This is the raw ${glossaryTerm('median')} price — the saw-tooth you actually pay.</p>
    </div>
    <div class="chart-scroll"><div class="chart-frame" id="weeks-frame"></div></div>
  `;
  if (!weeks.length) { host.querySelector('#weeks-frame')!.innerHTML = '<p class="empty">No complete weeks for this region.</p>'; return; }

  const width = Math.max(460, weeks.length * 9);
  const height = 260;
  const padL = 46; const padR = 14; const padT = 16; const padB = 40;
  const innerW = width - padL - padR;
  const innerH = height - padT - padB;
  const values = weeks.flatMap((w) => w.v);
  const lo = Math.min(...values) * 0.985;
  const hi = Math.max(...values) * 1.015;
  const xFor = (i: number) => padL + (i / Math.max(1, weeks.length * 7 - 1)) * innerW;
  const yFor = (v: number) => padT + innerH - ((v - lo) / (hi - lo)) * innerH;

  const svg = chartSvg(width, height, `Weekly prices for ${region.region}`);
  for (const t of ticks(lo, hi, 5)) {
    svg.appendChild(svgEl('line', { x1: padL, x2: padL + innerW, y1: yFor(t), y2: yFor(t), class: 'gridline' }));
    svg.appendChild(text(padL - 8, yFor(t) + 4, (t / 10).toFixed(0), { 'text-anchor': 'end', 'font-size': 11 }));
  }
  const pts: Array<[number, number]> = [];
  weeks.forEach((w, wi) => w.v.forEach((v, d) => pts.push([xFor(wi * 7 + d), yFor(v)])));
  svg.appendChild(svgEl('path', { d: linePath(pts), fill: 'none', stroke: '#0e7c6b', 'stroke-width': 1.4 }));

  weeks.forEach((w, wi) => {
    w.v.forEach((v, d) => {
      const isTrough = d === w.t;
      if (!isTrough && weeks.length > 26) return;
      const c = svgEl('circle', {
        cx: xFor(wi * 7 + d), cy: yFor(v), r: isTrough ? 2.6 : 1.4,
        fill: isTrough ? '#167a4a' : '#a8a094',
      });
      mark(c, `${DAY_NAMES[d]}, week of ${w.w}\n${cents(v)}c/L${isTrough ? '\ncheapest day of this week' : ''}`);
      svg.appendChild(c);
    });
  });
  svg.appendChild(text(padL, height - 8, `${weeks[0].w} → ${weeks[weeks.length - 1].w} · green dot = that week’s cheapest day`, { 'font-size': 11, fill: '#857f78' }));
  (host.querySelector('#weeks-frame') as HTMLElement).appendChild(svg);
  void escTip;
}
